import { useEffect, useRef, useState } from "react";
import { FaCompress, FaExpand, FaPause, FaPlay, FaVolumeUp } from "react-icons/fa";
import ReactPlayer from "react-player";

export default function CustomVideoPlayer({ currentItem, onComplete }) {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [showControls, setShowControls] = useState(true);
  const hideTimeoutRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [played, setPlayed] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1); // 🔹 Playback speed

  // track furthest watched time
  const [maxWatched, setMaxWatched] = useState(0);
  const maxWatchedRef = useRef(0);
  const durationRef = useRef(0);
  const currentItemRef = useRef(currentItem);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const resetHideTimer = () => {
    setShowControls(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => setShowControls(false), 1500);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = () => resetHideTimer();
    container.addEventListener("mousemove", handleMouseMove);

    // Start the hide timer initially
    resetHideTimer();

    return () => {
      container.removeEventListener("mousemove", handleMouseMove);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Keyboard: spacebar to toggle play/pause
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Spacebar
      if (e.code === "Space") {
        e.preventDefault(); // prevent page scroll
        togglePlay();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Keep refs in sync so Media Session handlers always see fresh values
  useEffect(() => { maxWatchedRef.current = maxWatched; }, [maxWatched]);
  useEffect(() => { durationRef.current = duration; }, [duration]);
  useEffect(() => { currentItemRef.current = currentItem; }, [currentItem]);

  useEffect(() => {
    // Reset playback state for new video lesson
    setPlayed(0);
    setPlayedSeconds(0);
    setMaxWatched(0);
    maxWatchedRef.current = 0;
    setPlaying(false);
  }, [currentItem]);

  // Block lock-screen forward seeking via Media Session API
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;

    const seekTo = (targetSeconds) => {
      const isCompleted = currentItemRef.current?.is_completed;
      const clamped = isCompleted
        ? targetSeconds
        : Math.min(targetSeconds, maxWatchedRef.current);
      playerRef.current?.seekTo(clamped, "seconds");
      setPlayedSeconds(clamped);
      setPlayed(durationRef.current ? clamped / durationRef.current : 0);
    };

    navigator.mediaSession.setActionHandler("play", () => setPlaying(true));
    navigator.mediaSession.setActionHandler("pause", () => setPlaying(false));
    navigator.mediaSession.setActionHandler("seekbackward", (d) => {
      const skip = d.seekOffset ?? 10;
      const internal = playerRef.current?.getInternalPlayer();
      const current = internal?.currentTime ?? 0;
      seekTo(Math.max(current - skip, 0));
    });
    navigator.mediaSession.setActionHandler("seekforward", (d) => {
      const skip = d.seekOffset ?? 10;
      const internal = playerRef.current?.getInternalPlayer();
      const current = internal?.currentTime ?? 0;
      seekTo(current + skip); // seekTo already clamps to maxWatched
    });
    navigator.mediaSession.setActionHandler("seekto", (d) => {
      if (d.seekTime != null) seekTo(d.seekTime);
    });

    return () => {
      ["play", "pause", "seekbackward", "seekforward", "seekto"].forEach((a) => {
        try { navigator.mediaSession.setActionHandler(a, null); } catch {}
      });
    };
  }, []);

  const togglePlay = () => setPlaying((prev) => !prev);

  const handleSeek = (e) => {
    const fraction = parseFloat(e.target.value);
    const seekToSeconds = fraction * duration;

    if (currentItem?.is_completed) {
      setPlayed(fraction);
      playerRef.current.seekTo(seekToSeconds, "seconds");
      return;
    }

    // Prevent forward seeking beyond max watched
    if (seekToSeconds > maxWatched) {
      const allowedFraction = maxWatched / duration;
      setPlayed(allowedFraction);
      playerRef.current.seekTo(maxWatched, "seconds");
      return;
    }

    // Normal seek
    setPlayed(fraction);
    playerRef.current.seekTo(seekToSeconds, "seconds");
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  useEffect(() => {
    if (playerRef.current) {
      const internal = playerRef.current.getInternalPlayer();
      if (internal) {
        internal.volume = volume;
      }
    }
  }, [volume]);
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [isFullscreen]);

  return (
    <>
      {currentItem.video && (
        <div
          dir="ltr"
          ref={containerRef}
          className={`mx-auto w-full overflow-hidden bg-black shadow-2xl ${
            isFullscreen
              ? "fullscreen-video fixed inset-0 z-[9999] rounded-none"
              : "relative aspect-video max-w-4xl rounded-2xl"
          }`}
        >
          {/* Click overlay to toggle play */}
          <div onClick={togglePlay} className="absolute inset-0 z-10 cursor-pointer" />

          <ReactPlayer
            ref={playerRef}
            url={currentItem.video}
            playing={playing}
            volume={volume}
            playbackRate={playbackRate}
            width="100%"
            height="100%"
            playsinline
            controls={false}
            onProgress={({ played, playedSeconds }) => {
              setPlayed(played);
              setPlayedSeconds(playedSeconds);

              if (!currentItem?.is_completed) {
                if (playedSeconds > maxWatched) setMaxWatched(playedSeconds);

                if (duration && playedSeconds >= duration - 0.5) {
                  onComplete?.();
                }
              }
            }}
            onDuration={(d) => setDuration(d)}
          />

          {/* Controls */}
          {showControls && (
            <div
              className="absolute bottom-0 left-0 z-20 flex w-full flex-col gap-2 bg-gradient-to-t from-black/80 to-transparent p-2 text-white transition-opacity duration-300 sm:p-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Progress */}
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={played}
                onChange={handleSeek}
                className="h-1 w-full cursor-pointer accent-slate-200 sm:h-2"
              />

              <div className="flex items-center justify-between">
                {/* Left side */}
                <div className="flex items-center gap-2 sm:gap-4">
                  <button onClick={togglePlay} className="text-base transition hover:scale-110 sm:text-lg">
                    {playing ? <FaPause /> : <FaPlay />}
                  </button>

                  {/* Time */}
                  <span className="text-[10px] font-medium sm:text-sm">
                    {formatTime(playedSeconds)} / {formatTime(duration)}
                  </span>

                  {/* Volume */}
                  <div className="flex items-center gap-1 sm:gap-2">
                    <FaVolumeUp className="text-xs sm:text-base" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onChange={(e) => setVolume(parseFloat(e.target.value))}
                      className="w-12 cursor-pointer accent-slate-200 sm:w-24"
                    />

                    {/* Playback speed */}
                    <select
                      value={playbackRate}
                      onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                      className="ml-1 rounded border border-gray-400 bg-black/50 px-1 py-[2px] text-[10px] text-white sm:ml-2 sm:text-sm"
                    >
                      {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                        <option key={rate} value={rate}>
                          {rate}x
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right side */}
                <button onClick={toggleFullscreen} className="text-lg transition hover:scale-110">
                  {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
